import {
  AllCommunityModule,
  CellClickedEvent,
  ColDef,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  ModuleRegistry,
  OverlaySelectorFunc,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import clsx from 'clsx';
import React, { useState } from 'react';

ModuleRegistry.registerModules([AllCommunityModule]);

import { Loader } from 'src/components/loading/Loader';
import { BaseActionsMenuProps } from 'src/constants';
/* eslint-disable no-restricted-imports -- temporary during ag-grid v27→v35 migration; shared infra will move out of legacy in Phase 5 */
// TODO: move BaseMenu, NoDataOverlay, and NoFilesStandardPage out of src/legacy and update these imports (no-legacy-imports-outside-legacy)
import { BaseMenu } from 'src/legacy/components/Dropdowns/BaseMenu';
import NoDataOverlay, {
  OverlayParams,
} from 'src/legacy/components/Overlays/NoDataOverlay';
import NoFilesStandardPage from 'src/legacy/components/UI/StandardPage/NoFilesStandardPage';
/* eslint-enable no-restricted-imports */

import { AgGridContext } from './AgGridContext';
import HeaderRender from './HeaderRender';
import { portalTheme } from './portalTheme';
import type { TableProps } from './Table.types';

import './Table.scss';

const baseDefaultColDef = {
  sortable: false,
  suppressHeaderMenuButton: true,
  suppressMovable: true,
};

const tableContainerStyle = {
  height: '100%',
  width: '100%',
};

const BlankOverlay = () => null;

const NoFilesStandardPageRenderer = ({
  onActionButtonOptionSelected,
  isFolderLockedForClient,
}: Pick<
  OverlayParams,
  'onActionButtonOptionSelected' | 'isFolderLockedForClient'
>) =>
  !onActionButtonOptionSelected ? null : (
    <NoFilesStandardPage
      onOptionSelected={onActionButtonOptionSelected}
      isFolderLockedForClient={isFolderLockedForClient}
    />
  );

type LegacyColDef = ColDef & {
  children?: LegacyColDef[];
};

const normalizeColDef = (col: LegacyColDef): ColDef => {
  const { children, ...rest } = col;
  const normalized: ColDef = rest;
  if (children) {
    (normalized as ColDef & { children: ColDef[] }).children =
      children.map(normalizeColDef);
  }
  return normalized;
};

const normalizeColumnDefs = (columnDefs: TableProps['columnDefs']) =>
  columnDefs.map((col: LegacyColDef): ColDef => normalizeColDef(col));

const Table = <TData = Record<string, unknown>,>({
  gridLoaded,
  rowData,
  columnDefs,
  searchKey,
  onRowClicked,
  onCellClicked,
  frameworkComponents,
  noRowsOverlayComponentName = '',
  defaultColDef = baseDefaultColDef,
  enableCellTextSelection = true,
  rowSelection = 'multiple',
  rowHeight = 50,
  headerHeight = 40,
  disableRowHover = false,
  overlayParams = {
    noRowsLabel: 'No data is added',
  },
  onCreateNewRow,
  getRowClass,
  // v27-only prop consumed here and translated to v35's getRowId below;
  // not forwarded to AgGridReact as-is.
  getRowNodeId,
  ...tableProps
}: TableProps<TData>): React.ReactElement => {
  const gridTableApi = React.useRef<GridApi>(undefined);
  const [actionMenuButtonProps, setActionMenuButtonProps] =
    useState<BaseActionsMenuProps | null>(null);
  const [showPinnedColumnShadow, setShowPinnedColumnShadow] = useState(false);
  const { setGridApi, gridApi } = React.useContext(AgGridContext);

  const handleActionMenuClick = React.useCallback(
    (menuOptions: BaseActionsMenuProps) => {
      const { menuProps, ...rest } = menuOptions;
      setActionMenuButtonProps({
        ...rest,
        menuProps: menuProps && {
          ...menuProps,
          onClose: () => setActionMenuButtonProps(null),
        },
      });
    },
    [],
  );

  const formattedColDefs = React.useMemo(() => {
    const normalized = normalizeColumnDefs(columnDefs);
    return normalized.map((col) => {
      if (col.headerName === 'Actions') {
        return {
          width: 48,
          headerClass: 'hiddenHeaderCell',
          cellClass: 'centeredColumn',
          ...col,
          cellRendererParams: {
            onActionMenuClick: handleActionMenuClick,
            ...col.cellRendererParams,
          },
        };
      }

      if (col.headerName === 'Creation date') {
        return {
          headerClass: 'centeredHeaderCell',
          ...col,
        };
      }

      return col;
    });
  }, [columnDefs, handleActionMenuClick]);

  React.useEffect(() => {
    if (gridApi && 'setGridOption' in gridApi) {
      gridApi.setGridOption('quickFilterText', searchKey ?? '');
    }
  }, [searchKey, gridApi]);

  const components = React.useMemo(
    () => ({
      ...frameworkComponents,
      agColumnHeader: HeaderRender,
      customLoadingOverlay: Loader,
      customNoRowsOverlay: NoDataOverlay,
      blankOverlay: BlankOverlay,
      NoFilesStandardPage: NoFilesStandardPageRenderer,
    }),
    [frameworkComponents],
  );

  const baseColDef = React.useMemo(
    () => ({ ...baseDefaultColDef, ...defaultColDef }),
    [defaultColDef],
  );

  const getRowId = React.useMemo(() => {
    if (!getRowNodeId) return undefined;
    return (params: GetRowIdParams) => getRowNodeId(params.data);
  }, [getRowNodeId]);

  const handleCellClicked = React.useCallback(
    (event: CellClickedEvent) => {
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) {
        return;
      }

      if (onCellClicked) {
        onCellClicked(event);
        return;
      }

      if (
        onRowClicked &&
        event.colDef.headerName !== 'Actions' &&
        event.colDef.colId !== 'checkbox'
      ) {
        onRowClicked(event.data);
      }
    },
    [onRowClicked, onCellClicked],
  );

  const handleShowPinnedColumnShadow = React.useCallback(() => {
    const gridCenter = document.querySelector('.ag-center-cols-viewport');
    if (!gridCenter) {
      return;
    }
    const next = gridCenter.scrollWidth > gridCenter.clientWidth;
    setShowPinnedColumnShadow((prev) => (prev === next ? prev : next));
  }, []);

  const handleResize = React.useCallback(() => {
    setTimeout(() => {
      if (gridTableApi.current) {
        gridTableApi.current.sizeColumnsToFit();
        handleShowPinnedColumnShadow();
      }
    });
  }, [handleShowPinnedColumnShadow]);

  React.useEffect(() => {
    if (!gridApi) return;
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [gridApi, handleResize]);

  const onGridReady = React.useCallback(
    (params: GridReadyEvent) => {
      gridTableApi.current = params.api;
      // Shared AgGridContext is typed against the legacy v27 GridApi; the
      // v35 GridApi is structurally different but functionally compatible
      // for the fields consumers read (gridApi.setQuickFilter etc.).
      setGridApi(params.api);
      params.api.sizeColumnsToFit();
      if (gridLoaded) {
        return gridLoaded(params.api);
      }
      if (onCreateNewRow) {
        return onCreateNewRow(params);
      }
      return null;
    },
    [gridLoaded, onCreateNewRow, setGridApi],
  );

  const handleColumnsChanged = React.useCallback(() => {
    if (gridTableApi.current) {
      gridTableApi.current.sizeColumnsToFit();
      handleShowPinnedColumnShadow();
    }
  }, [handleShowPinnedColumnShadow]);

  // v35 introduced a separate `noMatchingRows` overlay for the
  // filter/search empty state. Render nothing in that state so a
  // user searching/filtering doesn't see the "No data is added"
  // empty-dataset message (which only makes sense when rowData is
  // actually empty). Returning `undefined` for other states defers to
  // the `loadingOverlayComponent` / `noRowsOverlayComponent` props.
  const overlayComponentSelector = React.useMemo<OverlaySelectorFunc>(
    () => (params) => {
      if (params.overlayType === 'noMatchingRows') {
        return { component: 'blankOverlay' };
      }
      return undefined;
    },
    [],
  );

  return (
    <div
      className={clsx('ag-theme-portal', {
        'disable-hover-effect': disableRowHover,
        'show-sticky-cells-shadow': showPinnedColumnShadow,
      })}
      style={tableContainerStyle}
    >
      <AgGridReact
        theme={portalTheme}
        rowHeight={rowHeight}
        headerHeight={headerHeight}
        defaultColDef={baseColDef}
        components={components}
        columnDefs={formattedColDefs}
        getRowId={getRowId}
        onGridReady={onGridReady}
        suppressScrollOnNewData
        suppressAggFuncInHeader
        noRowsOverlayComponent={
          noRowsOverlayComponentName || 'customNoRowsOverlay'
        }
        loadingOverlayComponent="customLoadingOverlay"
        noRowsOverlayComponentParams={{
          overlayParams: () => overlayParams,
        }}
        overlayComponentSelector={overlayComponentSelector}
        suppressContextMenu
        rowData={rowData}
        rowSelection={rowSelection}
        enableCellTextSelection={enableCellTextSelection}
        onCellClicked={handleCellClicked}
        // v35: onDisplayedColumnsChanged is a superset of onGridColumnsChanged
        // (fires for visibility/resize/reorder as well as add/remove), so
        // using it alone covers TableColumnsSettings toggles and avoids a
        // double sizeColumnsToFit on structural changes.
        onDisplayedColumnsChanged={handleColumnsChanged}
        animateRows
        suppressRowClickSelection
        suppressCellFocus
        getRowClass={getRowClass}
        {...tableProps}
      />
      {actionMenuButtonProps && <BaseMenu {...actionMenuButtonProps} />}
    </div>
  );
};

export default Table;
